import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { ActionMenu, ConditionalRender, MenuItemType, TableColumnType, TableSwitch } from '@ui';
import { Hub, HubListFilters, RegionType } from '@/api/apis/facilities';
import { getFullName, getShortTzByIanaName } from '@/utils/helpers';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const HUBS_LIST_FILTERS_BASE: HubListFilters = {
  Ids: [],
  countries: [],
  states: [],
  timezones: [],
  regions: [],
  regionalManagers: [],
};

export const FILTERS_NAMES: Record<string, string> = {
  countries: 'Country',
  timezones: 'Timezone',
  regions: 'Region',
  states: 'State',
  regionalManagers: 'Regional manager name',
};

export const HUB_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'NOP hub name', {
    sortable: true,
    minWidthClassName: 'min-w-[220px]',
  }),
  {
    ...COMMON_COLUMN_PRESETS.code,
    minWidthClassName: 'min-w-[120px]',
  },
  COMMON_COLUMN_PRESETS.timezone,
  col('region', 'Region', {
    sortable: true,
    minWidthClassName: 'min-w-[120px]',
  }),
  {
    ...COMMON_COLUMN_PRESETS.address,
    minWidthClassName: 'min-w-[240px]',
  },
  {
    ...COMMON_COLUMN_PRESETS.city,
    minWidthClassName: 'min-w-[160px]',
  },
  {
    ...COMMON_COLUMN_PRESETS.state,
    minWidthClassName: 'min-w-[133px]',
  },
  {
    ...COMMON_COLUMN_PRESETS.postalCode,
    minWidthClassName: 'min-w-[120px]',
  },
  col('regionalManager', 'Regional manager', {
    sortable: false,
    minWidthClassName: 'min-w-[240px]',
  }),
  col('isSuperHub', 'Super Hub', {
    sortable: false,
    minWidthClassName: 'min-w-[92px]',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getActionsMenu = (onEdit: () => void, onDelete?: () => void): MenuItemType[] => [
  {
    key: 'edit-hub',
    type: 'action',
    to: 'edit-hub',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  ...(onDelete
    ? [
        {
          key: 'delete-hub',
          type: 'action',
          to: 'delete-hub',
          label: (
            <div className="flex items-center gap-2 text-danger">
              <MdOutlineDelete size={16} />
              <span className="text-m-r">Delete</span>
            </div>
          ),
          onAction: () => onDelete(),
        } as MenuItemType,
      ]
    : []),
];

type GetRenderHubTableCellProps = {
  onEdit: (hubId: string) => void;
  onDelete: (hubId: string) => void;
  onSuperhubChange: (hub: Hub) => void;
  regionsList?: RegionType[];
  hasCreatePermissions: boolean;
  hasRemovePermissions: boolean;
  resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined;
};

export const getRenderHubTableCell =
  ({
    regionsList,
    onEdit,
    onDelete,
    onSuperhubChange,
    hasCreatePermissions,
    hasRemovePermissions,
    resolveSubdivisionName,
  }: GetRenderHubTableCellProps) =>
  (hub: Hub, columnKey: Key): ReactNode => {
    const { id, image, name, code, location, regionalManager, isSuperHub } = hub;
    const isRegionalManager = !!regionalManager?.firstName && !!regionalManager?.lastName;

    const onHubEdit = () => onEdit(id);
    const onHubDelete = () => onDelete(id);

    const ACTIONS_MENU = getActionsMenu(onHubEdit, hasRemovePermissions ? onHubDelete : undefined);
    const currentRegion = regionsList?.find((r) => r.id === location.regionId);

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/hubs/locations/${id}`} className="flex items-center gap-2">
            <span className="text-m-m">{name}</span>
          </Link>
        );
      case 'code':
        return <span className="text-m-r uppercase">{code}</span>;
      case 'timezone':
        return <span className="text-m-r uppercase">{getShortTzByIanaName(location?.timezone) || '-'}</span>;
      case 'region':
        return <span className="text-m-r">{currentRegion?.name}</span>;
      case 'address':
        return <span className="text-m-r">{location.addressLine}</span>;
      case 'city':
        return <span className="text-m-r">{location.city}</span>;
      case 'state':
        return (
          <span className="text-m-r uppercase">{resolveSubdivisionName(location.state, location.country) || '-'}</span>
        );
      case 'postalCode':
        return <span className="text-m-r">{location.postalCode}</span>;
      case 'regionalManager':
        return (
          <div className="flex items-center gap-2">
            <ConditionalRender show={isRegionalManager} placeholder="-">
              <TmdxAvatar
                radius="xl"
                size="xl"
                name={getFullName(regionalManager?.firstName, regionalManager?.lastName)}
                src={image}
              />
              <span className="text-m-m">{getFullName(regionalManager?.firstName, regionalManager?.lastName)}</span>
            </ConditionalRender>
          </div>
        );
      case 'isSuperHub':
        return (
          <TableSwitch isActive={isSuperHub} isDisabled={!hasCreatePermissions} action={() => onSuperhubChange(hub)} />
        );
      case 'actions':
        return (
          <ActionMenu
            isDisabled={!hasCreatePermissions && !hasRemovePermissions}
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${name} actions menu`}
          />
        );
      default:
        return null;
    }
  };
